import cx from 'classnames';
import React, { useMemo, useRef } from 'react';
import Button from '../Button';
import Icon from '../Icon';
import styles from './TextInput.module.scss';
import usePresenter from './TextInput.presenter';
import getProps from './TextInput.props';
import type { TextInputCombinedProps } from './types';

const TextInput: React.FC<TextInputCombinedProps> = (props) => {
  const {
    state,
    style,
    size,
    className,
    classes,
    textValue,
    textPlaceholder,
    onTextChanged,
    onClick,
    onKeyDown,
    inputType,
    maxLength,
    disabled,
    icon,
    button,
    ariaLabel,
    ariaRole,
    inputRef,
    isAutoFocused,
    removeChar,
    name,
    onBlurChanged,
    onFocusChanged,
    ...otherProps
  } = usePresenter(props);

  const variantName = `${state}${style}${size}`;
  const internalProps = getProps(variantName);
  const currentStyle = styles[`textInput${variantName}`];

  const inputValue: string | number = useMemo(() => {
    return inputType === 'number'
      ? `${textValue || 0}`
      : textValue || '';
  }, [inputType, textValue]);

  const localRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (removeChar && inputRef && inputRef.current) {
      inputRef.current.value = event.target.value.replace(removeChar, '');
    } else if (removeChar && localRef.current) {
      localRef.current.value = event.target.value.replace(removeChar, '');
    }
    if (onTextChanged) {
      onTextChanged(event);
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (onFocusChanged) {
      onFocusChanged(event);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (onBlurChanged) {
      onBlurChanged(event);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (onKeyDown) {
      onKeyDown(event);
      return;
    }

    if (event.key === 'Enter') {
      const targetElement = event.target as HTMLInputElement;
      const form = targetElement?.form;
      if (form) {
        const index = Array.prototype.indexOf.call(form, event.target);
        (form.elements[index + 1] as HTMLElement).focus();
        (form.elements[index + 1] as HTMLElement).click();
        event.preventDefault();
      }
      // upon enter we must check if there is any blur event waiting to change state
      if (onBlurChanged) {
        onBlurChanged(event as unknown as React.FocusEvent<HTMLInputElement>);
      }
    }
  };

  let componentView: JSX.Element;

  switch (variantName) {
    case 'EmptyClearWithIconRegular': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <Icon
            className={cx(styles.icon, classes?.icon)}
            {...internalProps.icon}
            {...icon} />
          <input
            className={cx(styles.input, classes?.input)}
            ref={inputRef || localRef}
            autoFocus={isAutoFocused}
            aria-label={ariaLabel}
            role={ariaRole}
            placeholder={textPlaceholder}
            value={inputValue}
            onChange={handleChange}
            onKeyDown={handleKeyPress}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onClick={onClick}
            disabled={disabled}
            type={inputType}
            maxLength={maxLength}
            name={name}
            {...otherProps}
          />
        </div>
      );
      break;
    }
    case 'EmptyDefaultCenterRegular':
    case 'EmptyDefaultRegular':
    case 'EmptyDefaultSmall':
    case 'FilledDefaultCenterRegular':
    case 'FilledDefaultRegular':
    case 'FilledDefaultSmall':
    case 'EmptyClearRegular': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <input
            className={cx(styles.input, classes?.input)}
            ref={inputRef || localRef}
            autoFocus={isAutoFocused}
            aria-label={ariaLabel}
            role={ariaRole}
            placeholder={textPlaceholder}
            value={inputValue}
            onChange={handleChange}
            onKeyDown={handleKeyPress}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onClick={onClick}
            disabled={disabled}
            type={inputType}
            maxLength={maxLength}
            name={name}
            {...otherProps}
          />
        </div>
      );
      break;
    }
    case 'FilledClearRegular': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <input
            className={cx(styles.input, classes?.input)}
            ref={inputRef || localRef}
            autoFocus={isAutoFocused}
            aria-label={ariaLabel}
            role={ariaRole}
            placeholder={textPlaceholder}
            value={inputValue}
            onChange={handleChange}
            onKeyDown={handleKeyPress}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onClick={onClick}
            disabled={disabled}
            type={inputType}
            maxLength={maxLength}
            name={name}
            {...otherProps}
          />
          <Button
            className={cx(styles.button, classes?.button)}
            {...internalProps.button}
            {...button} />
        </div>
      );
      break;
    }
    default:
      componentView = <div>�</div>;
      break;
  }

  return componentView;
};

TextInput.displayName = 'TextInput';
export default TextInput;
